import { useState } from 'react';
import { Plus, X, ArrowsClockwise } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';
import type { Layer } from '@spine-workspace/shared';

const TRACK_SPEEDS = [0.5, 1, 2] as const;

interface AnimationTracksProps {
  layer: Layer;
  animations: string[];
}

export default function AnimationTracks({ layer, animations }: AnimationTracksProps) {
  const { setLayerAnimation, addAnimationTrack, removeAnimationTrack, setTrackTimeScale } = useSceneStore();
  const [mixDuration, setMixDuration] = useState(0.3);

  function handleAnimChange(trackIndex: number, animName: string) {
    const track = layer.animation.tracks.find((t) => t.trackIndex === trackIndex);
    const loop = track?.loop ?? true;
    const timeScale = track?.timeScale ?? 1;
    // Apply mix duration before switching so the transition crossfades
    if (track && mixDuration > 0) {
      getSpineManager().setDefaultMix(layer.id, mixDuration);
    }
    setLayerAnimation(layer.id, trackIndex, animName, loop);
    getSpineManager().setAnimation(layer.id, trackIndex, animName, loop, timeScale);
  }

  function handleToggleLoop(trackIndex: number) {
    const track = layer.animation.tracks.find((t) => t.trackIndex === trackIndex);
    if (!track) return;
    const newLoop = !track.loop;
    setLayerAnimation(layer.id, trackIndex, track.name, newLoop);
    getSpineManager().setTrackLoop(layer.id, trackIndex, newLoop);
  }

  function handleTimeScale(trackIndex: number, timeScale: number) {
    setTrackTimeScale(layer.id, trackIndex, timeScale);
    getSpineManager().setTrackTimeScale(layer.id, trackIndex, timeScale);
  }

  function handleAddTrack() {
    if (animations.length === 0) return;
    const name = animations[0];
    addAnimationTrack(layer.id, name);
    const nextIndex = layer.animation.tracks.length;
    getSpineManager().setAnimation(layer.id, nextIndex, name, true, 1);
  }

  function handleRemoveTrack(trackIndex: number) {
    removeAnimationTrack(layer.id, trackIndex);
    getSpineManager().clearTrack(layer.id, trackIndex);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <span className="font-sans text-xs font-medium text-text-secondary">Tracks</span>
        <button
          onClick={handleAddTrack}
          className="flex items-center gap-1 text-xs font-sans text-accent hover:text-accent-hover transition-colors duration-fast"
        >
          <Plus size={12} />
          Add Track
        </button>
      </div>

      {layer.animation.tracks.length === 0 ? (
        <p className="text-text-tertiary text-xs font-sans">No tracks — click Add Track</p>
      ) : (
        <div className="space-y-2">
          {layer.animation.tracks.map((track) => (
            <div key={track.trackIndex} className="space-y-1">
              {/* Row 1: track index + animation selector + loop + remove */}
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-text-tertiary w-5 shrink-0">T{track.trackIndex}</span>
                <select
                  className="flex-1 min-w-0 bg-bg-input border border-border text-text-primary rounded-md
                             px-2 py-[5px] text-xs font-sans
                             hover:bg-bg-hover focus:outline-none focus:border-accent
                             transition-all duration-fast"
                  value={track.name}
                  onChange={(e) => handleAnimChange(track.trackIndex, e.target.value)}
                >
                  {animations.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                <button
                  title={track.loop ? 'Looping — click to disable' : 'Not looping — click to enable'}
                  onClick={() => handleToggleLoop(track.trackIndex)}
                  className={`w-[26px] h-[26px] flex items-center justify-center rounded transition-all duration-fast shrink-0 ${
                    track.loop ? 'text-accent bg-accent-muted' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >
                  <ArrowsClockwise size={12} />
                </button>
                {track.trackIndex > 0 && (
                  <button
                    title="Remove track"
                    onClick={() => handleRemoveTrack(track.trackIndex)}
                    className="w-[26px] h-[26px] flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all duration-fast shrink-0"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              {/* Row 2: per-track speed */}
              <div className="flex items-center gap-1 pl-7">
                <span className="text-xxs font-sans text-text-tertiary mr-1">Speed</span>
                {TRACK_SPEEDS.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleTimeScale(track.trackIndex, s)}
                    className={`px-[5px] py-[2px] rounded text-xs font-mono transition-all duration-fast ${
                      (track.timeScale ?? 1) === s
                        ? 'bg-accent-muted text-accent border border-accent/30'
                        : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                    }`}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Crossfade mix duration */}
      {layer.animation.tracks.length > 0 && (
        <div className="flex items-center gap-2 pt-1 border-t border-border-subtle mt-2">
          <span className="font-sans text-xs text-text-tertiary shrink-0">Mix</span>
          <input
            type="range"
            min={0}
            max={2}
            step={0.05}
            value={mixDuration}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setMixDuration(v);
              getSpineManager().setDefaultMix(layer.id, v);
            }}
            className="flex-1 h-1 rounded-full appearance-none bg-bg-active cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-text-primary
              [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent"
          />
          <span className="font-mono text-xs text-text-tertiary w-[32px] text-right shrink-0">
            {mixDuration.toFixed(2)}s
          </span>
        </div>
      )}
    </div>
  );
}
